/**
 * Query key factories for cache keys and invalidation.
 * Use these everywhere so invalidateQueries and cache behavior are consistent.
 */

import type { ListTenantsParams } from "@/services/platformTenantsService";

export const clientKeys = {
  all: ["clients"] as const,
  /** Full client list for dropdowns (paginates API max page size). */
  listForSelect: () => ["clients", "list-for-select"] as const,
  list: (params: {
    page: number;
    limit: number;
    search?: string;
    documentType?: string;
    defaultCurrency?: string;
  }) => ["clients", "list", params] as const,
  detail: (id: string) => ["clients", "detail", id] as const,
  catalog: (clientId: string) => ["clients", "catalog", clientId] as const,
};

export const productKeys = {
  all: ["products"] as const,
  list: () => ["products", "list"] as const,
};

export const brandKeys = {
  all: ["brands"] as const,
  list: () => ["brands", "list"] as const,
};

export const bandejaKeys = {
  all: ["accountant", "inbox"] as const,
  composite: () => ["accountant", "inbox", "composite"] as const,
  acks: (params: { limit?: number; tenant?: string } = {}) =>
    ["accountant", "inbox", "acks", params] as const,
  boletos: (params: { limit?: number; tenant?: string } = {}) =>
    ["accountant", "inbox", "boletos", params] as const,
  obligations: (params: { limit?: number; days?: number; tenant?: string } = {}) =>
    ["accountant", "inbox", "obligations", params] as const,
};

export const supplierKeys = {
  all: ["suppliers"] as const,
  list: (params: {
    page: number;
    limit: number;
    search?: string;
    documentType?: string;
    autoAckCfeOnReceive?: boolean;
  }) => ["suppliers", "list", params] as const,
  detail: (id: string) => ["suppliers", "detail", id] as const,
  spendSummary: (id: string) =>
    ["suppliers", "spend-summary", id] as const,
};

export const caeKeys = {
  all: ["cae"] as const,
  list: () => ["cae", "list"] as const,
};

export const recurringInvoiceKeys = {
  all: ["recurringInvoices"] as const,
  list: (params: { page: number; limit: number }) =>
    ["recurringInvoices", "list", params] as const,
  detail: (id: string) => ["recurringInvoices", "detail", id] as const,
  pendingForInvoice: (invoiceId: string) =>
    ["recurringInvoices", "pending", invoiceId] as const,
};

export const invoiceKeys = {
  all: ["invoices"] as const,
  list: (params: {
    page: number;
    limit: number;
    status?: string;
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    customerId?: string;
    currency?: string;
    createdByUserId?: string;
  }) => ["invoices", "list", params] as const,
  detail: (id: string) => ["invoices", "detail", id] as const,
  customerSummary: (customerId: string) =>
    ["invoices", "customer-summary", customerId] as const,
  recibidas: () => ["invoices", "recibidas"] as const,
  recibidaDetail: (id: string) => ["invoices", "recibidas", "detail", id] as const,
};

export const expenseKeys = {
  all: ["expenses"] as const,
  list: (params: {
    page: number;
    limit: number;
    dateFrom?: string;
    dateTo?: string;
    currency?: string;
    search?: string;
    supplierId?: string;
  }) => ["expenses", "list", params] as const,
  detail: (id: string) => ["expenses", "detail", id] as const,
};

export const userKeys = {
  all: ["users"] as const,
  list: (params: { page: number; limit: number }) =>
    ["users", "list", params] as const,
  pendingInvites: () => ["users", "pendingInvites"] as const,
};

export const tenantMemberKeys = {
  all: (tenantId: string) => ["tenantMembers", tenantId] as const,
  list: (tenantId: string) => ["tenantMembers", "list", tenantId] as const,
  pendingInvites: (tenantId: string) =>
    ["tenantMembers", "pendingInvites", tenantId] as const,
};

export const tenantKeys = {
  all: ["tenants"] as const,
  list: (params: ListTenantsParams) => ["tenants", "list", params] as const,
  detail: (id: string) => ["tenants", "detail", id] as const,
  overview: (id: string) => ["tenants", "overview", id] as const,
  current: (tenantId?: string) =>
    tenantId
      ? (["tenants", "current", tenantId] as const)
      : (["tenants", "current"] as const),
  documents: (id?: string) =>
    id ? (["tenants", "documents", id] as const) : (["tenants", "current", "documents"] as const),
  supportMessages: (tenantId?: string) =>
    tenantId
      ? (["tenants", "support-messages", tenantId] as const)
      : (["tenants", "current", "support-messages"] as const),
  cae: (tenantId: string) => ["tenants", "cae", tenantId] as const,
  apiKeys: () => ["tenants", "current", "api-keys"] as const,
  invoiceWebhook: () => ["tenants", "current", "invoice-webhook"] as const,
  apiIntegrationUsage: () => ["tenants", "current", "api-integration-usage"] as const,
};

export const dashboardKeys = {
  all: ["dashboard"] as const,
  metrics: () => ["dashboard", "metrics"] as const,
  exchangeRates: (date?: string) => ["dashboard", "exchangeRates", date] as const,
};

export const estadoFiscalKeys = {
  all: ["estado-fiscal"] as const,
  financial: () => ["estado-fiscal", "financial"] as const,
  history: () => ["estado-fiscal", "iva-history"] as const,
};

export const configKeys = {
  all: ["config"] as const,
};

export const deelKeys = {
  all: ["deel"] as const,
  status: () => ["deel", "status"] as const,
  contracts: () => ["deel", "contracts"] as const,
};

export const mercadopagoKeys = {
  all: ["mercadopago"] as const,
  status: () => ["mercadopago", "status"] as const,
  errors: () => ["mercadopago", "errors"] as const,
};

